import { ListMagnifyingGlassIcon } from '@phosphor-icons/react'
import { type ScaCounts, type Tool } from '../../utils/api-client/api-schemas'
import { TooltipWithHover } from '../tooltip'
import { type ScaSegment, mapScaToLabels, mapScaToSegments } from './composition-utils'
import { SegmentsBar, type SegmentsBarSize } from './segments-bar'
import * as barStyles from './segments-bar.css'
import { SegmentsLabels } from './segments-labels'
import * as labelStyles from './segments-labels.css'

type ScaCompositionBarProps = {
  statistics: ScaCounts
  total: number
  tool: Tool
  size?: SegmentsBarSize
  isTransitioning?: boolean
  onSegmentClick?: (label: ScaSegment) => unknown
  selectedSegments?: Iterable<ScaSegment>
  nonClickableSegments?: ScaSegment[]
  visibleSegments?: ScaSegment[]
}

export const ScaCompositionBar: React.FC<ScaCompositionBarProps> = ({
  statistics,
  total,
  tool,
  size = 'tiny',
  isTransitioning,
  onSegmentClick,
  selectedSegments = new Set<ScaSegment>(),
  nonClickableSegments = [],
  visibleSegments,
}) => {
  const selectedSegmentsSet = selectedSegments instanceof Set ? selectedSegments : new Set(selectedSegments)
  const allSegments = mapScaToSegments(statistics, total, tool)

  if (isTransitioning) {
    const skeletonSegments = allSegments.map(s => ({
      ariaLabel: s.ariaLabel,
      percentage: s.percentage,
      color: '',
      className: barStyles.skeletonSegment,
    }))
    return <SegmentsBar segments={skeletonSegments} size={size} />
  }

  const allLabels = mapScaToLabels(statistics, total, tool)
  const labels = visibleSegments ? allLabels.filter(l => visibleSegments.includes(l.name)) : allLabels

  const hasActiveFilters = selectedSegmentsSet.size > 0
  const isHiddenByQuickView = (ariaLabel: string) =>
    visibleSegments ? !visibleSegments.includes(ariaLabel as ScaSegment) : false
  const segments = allSegments.map(segment => ({
    ...segment,
    pressed: selectedSegmentsSet.has(segment.ariaLabel),
    dimmed: isHiddenByQuickView(segment.ariaLabel) || (hasActiveFilters && !selectedSegmentsSet.has(segment.ariaLabel)),
    onClick:
      onSegmentClick && !nonClickableSegments.includes(segment.ariaLabel) && !isHiddenByQuickView(segment.ariaLabel)
        ? () => onSegmentClick(segment.ariaLabel)
        : undefined,
  }))

  return (
    <TooltipWithHover
      trigger={<SegmentsBar segments={segments} size={size} />}
      maxWidth={1200}
      side="bottom"
      sideOffset={8}
      bare
    >
      <SegmentsLabels
        labels={labels}
        icon={<ListMagnifyingGlassIcon className={labelStyles.leadingIconSvg} weight="light" />}
      />
    </TooltipWithHover>
  )
}
